import { useMemo } from "react";
import { CreditCardIcon, LinkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { Badge, Button, Input, Modal, Spinner } from "../../../shared";
import {
  formatBillingDate,
  formatMoneyFromCents,
  getInvoiceAmountCents,
  getInvoiceDescription,
  getInvoiceStatusDisplay,
  getManualInvoiceViewUrl,
} from "./billing.utils";

const InvoiceLinkPanel = ({
  title,
  description,
  url,
  icon: Icon,
  copyLabel = "Copy link",
  openLabel = "Open",
  primaryOpen = false,
}) => {
  const copyUrl = async () => {
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Could not copy the link.");
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Icon className="h-4 w-4 text-gray-500" />
        {title}
      </div>
      <p className="mt-1 text-xs text-gray-600">{description}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input readOnly value={url} className="flex-1" />
        <div className="flex gap-2">
          <Button type="light" onClick={copyUrl}>
            {copyLabel}
          </Button>
          <Button
            type={primaryOpen ? "primary" : "alternative"}
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            {openLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Detail modal for a manual or hosted subscription invoice.
 */
export const BillingInvoiceDetailModal = ({
  visible = false,
  invoice = null,
  loading = false,
  onClose,
  renderActions,
  onResendNotification,
  resendNotificationLoading = false,
}) => {
  const viewUrl = useMemo(
    () => (invoice?.source === "manual" ? getManualInvoiceViewUrl(invoice) : null),
    [invoice]
  );
  const paymentUrl =
    invoice?.source === "manual" && invoice?.paymentUrl ? invoice.paymentUrl : null;

  if (!visible) {
    return null;
  }

  const statusDisplay = getInvoiceStatusDisplay(invoice?.status);
  const amountCents = getInvoiceAmountCents(invoice);
  const title =
    invoice?.number || getInvoiceDescription(invoice) || "Invoice";
  const subtitle =
    invoice?.number && invoice?.description ? invoice.description : null;
  const canResendNotification =
    Boolean(onResendNotification) &&
    invoice?.source === "manual" &&
    invoice?.status === "open" &&
    Boolean(invoice?.dueDate);

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title="Invoice details"
      size="lg"
      hideFooter
    >
      {loading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : !invoice ? (
        <p className="text-sm text-gray-600">Invoice not found.</p>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {subtitle ? (
                <p className="text-sm text-gray-500">{subtitle}</p>
              ) : null}
            </div>
            <Badge color={statusDisplay.color} size="lg">
              {statusDisplay.label}
            </Badge>
          </div>

          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {invoice.number ? (
              <div>
                <dt className="text-sm text-gray-500">Invoice number</dt>
                <dd className="text-base font-medium text-gray-900">{invoice.number}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-sm text-gray-500">Amount</dt>
              <dd className="text-base font-semibold text-gray-900">
                {formatMoneyFromCents(amountCents)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Payment due</dt>
              <dd className="text-base font-medium text-gray-900">
                {formatBillingDate(invoice.dueDate) || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Billing period start</dt>
              <dd className="text-base font-medium text-gray-900">
                {formatBillingDate(invoice.billingPeriodStart) || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Billing period end</dt>
              <dd className="text-base font-medium text-gray-900">
                {formatBillingDate(invoice.billingPeriodEnd) || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Issued</dt>
              <dd className="text-base font-medium text-gray-900">
                {formatBillingDate(invoice.createdAt || invoice.created) || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Paid on</dt>
              <dd className="text-base font-medium text-gray-900">
                {formatBillingDate(invoice.paidAt) || "—"}
              </dd>
            </div>
          </dl>

          {paymentUrl ? (
            <InvoiceLinkPanel
              title="Pay online"
              description="External payment link for this invoice. Customers can use this to pay by card, ACH, or your chosen payment provider."
              url={paymentUrl}
              icon={CreditCardIcon}
              copyLabel="Copy payment link"
              openLabel="Pay now"
              primaryOpen
            />
          ) : null}

          {viewUrl ? (
            <InvoiceLinkPanel
              title="View in AttuneLogic"
              description="Opens Settings → Billing and shows this invoice in the customer account."
              url={viewUrl}
              icon={LinkIcon}
              copyLabel="Copy view link"
              openLabel="Open"
            />
          ) : null}

          {invoice.notes ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <div className="font-medium text-gray-900">Notes</div>
              <p className="mt-1 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          ) : null}

          {invoice.source === "manual" && invoice.notificationSentAt ? (
            <p className="text-xs text-gray-500">
              Payment reminder email sent {formatBillingDate(invoice.notificationSentAt)}.
            </p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            {canResendNotification ? (
              <Button
                type="light"
                onClick={() => onResendNotification(invoice)}
                disabled={resendNotificationLoading}
              >
                {resendNotificationLoading ? "Sending..." : "Resend payment email"}
              </Button>
            ) : null}
            {renderActions ? renderActions(invoice) : null}
            <Button type="alternative" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
